import type {PositionHistoryEntry, HeatmapType, HeatmapCell} from '../types/rtlsTypes';

interface MineExtents {
  x: [number, number];
  y: [number, number];
  z: [number, number];
}

const GRID_SIZE = 50; // cells per axis — fine enough to follow tunnel paths

export function computeHeatmap(
  history: Record<string, PositionHistoryEntry[]>,
  type: HeatmapType,
  extents: MineExtents,
  filterLevel?: number | 'all',
  levelZRanges?: Record<number, {min: number; max: number}>,
): HeatmapCell[] {
  // Grid over X and Y (horizontal plane) — Z is the vertical/up axis
  const xMin = extents.x[0];
  const xMax = extents.x[1];
  const yMin = extents.y[0];
  const yMax = extents.y[1];
  const xStep = (xMax - xMin) / GRID_SIZE;
  const yStep = (yMax - yMin) / GRID_SIZE;

  // Collect positions filtered by level
  const allPositions: {x: number; y: number; z: number; timestamp: number; assetId: string}[] = [];
  for (const [assetId, entries] of Object.entries(history)) {
    for (const entry of entries) {
      if (filterLevel != null && filterLevel !== 'all' && levelZRanges) {
        const range = levelZRanges[filterLevel];
        if (range && (entry.position.z < range.min || entry.position.z > range.max)) {
          continue;
        }
      }
      allPositions.push({
        ...entry.position,
        timestamp: entry.timestamp,
        assetId,
      });
    }
  }

  if (allPositions.length === 0) {
    return [];
  }

  // Initialize grid
  const grid: number[][] = Array.from({length: GRID_SIZE}, () =>
    Array(GRID_SIZE).fill(0),
  );

  switch (type) {
    case 'density':
      computeDensity(grid, allPositions, xMin, yMin, xStep, yStep);
      break;
    case 'dwell':
      computeDwell(grid, history, xMin, yMin, xStep, yStep, filterLevel, levelZRanges);
      break;
    case 'traffic':
      computeTraffic(grid, history, xMin, yMin, xStep, yStep, filterLevel, levelZRanges);
      break;
  }

  // Normalize to 0-1 and build cells
  let maxVal = 0;
  for (let gx = 0; gx < GRID_SIZE; gx++) {
    for (let gy = 0; gy < GRID_SIZE; gy++) {
      maxVal = Math.max(maxVal, grid[gx][gy]);
    }
  }

  if (maxVal === 0) {
    return [];
  }

  // Log-normalize instead of linear. With sparse data (e.g. only ~67 trail
  // points across a 50×50 grid) a single hot cell dominates maxVal and every
  // other cell's linear-normalized intensity drops below the painter's 0.05
  // threshold — so the visible result is one solid red dot with no gradient.
  // log1p(value)/log1p(maxVal) compresses the high end and lifts the low
  // end so the yellow→red ramp actually has cells across its range.
  const logMax = Math.log1p(maxVal);
  const cells: HeatmapCell[] = [];
  for (let gx = 0; gx < GRID_SIZE; gx++) {
    for (let gy = 0; gy < GRID_SIZE; gy++) {
      const raw = grid[gx][gy];
      if (raw <= 0) continue;
      const intensity = Math.log1p(raw) / logMax;
      if (intensity > 0.01) {
        cells.push({
          x: xMin + (gx + 0.5) * xStep,
          y: yMin + (gy + 0.5) * yStep,
          z: 0, // Not used for horizontal grid sampling
          intensity,
        });
      }
    }
  }

  return cells;
}

function toGridCoord(
  val: number,
  min: number,
  step: number,
): number {
  const idx = Math.floor((val - min) / step);
  return Math.max(0, Math.min(GRID_SIZE - 1, idx));
}

// Spread value to cell and its immediate neighbors with Gaussian falloff
const SPREAD_RADIUS = 1;
const SPREAD_WEIGHTS = [
  [0.05, 0.15, 0.05],
  [0.15, 1.00, 0.15],
  [0.05, 0.15, 0.05],
];

function addToGridWithSpread(grid: number[][], gx: number, gy: number, value: number): void {
  for (let dx = -SPREAD_RADIUS; dx <= SPREAD_RADIUS; dx++) {
    for (let dy = -SPREAD_RADIUS; dy <= SPREAD_RADIUS; dy++) {
      const nx = gx + dx;
      const ny = gy + dy;
      if (nx >= 0 && nx < GRID_SIZE && ny >= 0 && ny < GRID_SIZE) {
        grid[nx][ny] += value * SPREAD_WEIGHTS[dx + 1][dy + 1];
      }
    }
  }
}

function computeDensity(
  grid: number[][],
  positions: {x: number; y: number}[],
  xMin: number,
  yMin: number,
  xStep: number,
  yStep: number,
): void {
  for (const pos of positions) {
    const gx = toGridCoord(pos.x, xMin, xStep);
    const gy = toGridCoord(pos.y, yMin, yStep);
    addToGridWithSpread(grid, gx, gy, 1);
  }
}

function computeDwell(
  grid: number[][],
  history: Record<string, PositionHistoryEntry[]>,
  xMin: number,
  yMin: number,
  xStep: number,
  yStep: number,
  filterLevel?: number | 'all',
  levelZRanges?: Record<number, {min: number; max: number}>,
): void {
  for (const entries of Object.values(history)) {
    for (let i = 1; i < entries.length; i++) {
      const prev = entries[i - 1];
      const curr = entries[i];
      if (filterLevel != null && filterLevel !== 'all' && levelZRanges) {
        const range = levelZRanges[filterLevel];
        if (range && (curr.position.z < range.min || curr.position.z > range.max)) {
          continue;
        }
      }
      const dt = curr.timestamp - prev.timestamp;
      const gx = toGridCoord(curr.position.x, xMin, xStep);
      const gy = toGridCoord(curr.position.y, yMin, yStep);
      addToGridWithSpread(grid, gx, gy, dt);
    }
  }
}

function computeTraffic(
  grid: number[][],
  history: Record<string, PositionHistoryEntry[]>,
  xMin: number,
  yMin: number,
  xStep: number,
  yStep: number,
  filterLevel?: number | 'all',
  levelZRanges?: Record<number, {min: number; max: number}>,
): void {
  for (const entries of Object.values(history)) {
    const visitedCells = new Set<string>();
    for (const entry of entries) {
      if (filterLevel != null && filterLevel !== 'all' && levelZRanges) {
        const range = levelZRanges[filterLevel];
        if (range && (entry.position.z < range.min || entry.position.z > range.max)) {
          continue;
        }
      }
      const gx = toGridCoord(entry.position.x, xMin, xStep);
      const gy = toGridCoord(entry.position.y, yMin, yStep);
      const key = `${gx},${gy}`;
      if (!visitedCells.has(key)) {
        visitedCells.add(key);
        addToGridWithSpread(grid, gx, gy, 1);
      }
    }
  }
}
